import { ArrowDown, ArrowUp, Plus, RotateCcw, SquarePen, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useResetFollowupSettings, useUpdateFollowupSettings } from "@/api/mutations";
import { useFollowupSettings } from "@/api/queries";
import type { FollowupRule, FollowupRuleAction, FollowupSettingsValue, FollowupSettingsView } from "@/api/types";
import { usePermissions } from "@/auth/permissions";
import { Section } from "@/components/feedback/key-value";
import { QueryState } from "@/components/feedback/query-state";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { formatDateTime } from "@/lib/dates";
import { followupSettingsErrors, newRule } from "@/lib/followup-rules";
import { formErrorsFrom } from "@/lib/form-errors";
import { STAGE_LABEL } from "@/lib/status";
import { FollowupCatchUp } from "./followup-catch-up";

const ACTION_LABEL: Record<FollowupRuleAction, string> = {
  CHECK_IN: "Rogue checks in",
  OFFER_AGAIN: "Rogue offers again",
  RERUN_API_OFFER: "Refresh the API offer, then offer again",
};
const MAX_OFFER_LABEL: Record<FollowupRule["when"]["maxOffer"], string> = {
  ANY: "With or without a max offer",
  SET: "Max offer set",
  NOT_SET: "No max offer yet",
};
const OFFER_LABEL: Record<FollowupRule["when"]["offer"], string> = {
  ANY: "With or without an offer",
  MADE: "After an offer was made",
  NONE: "Before any offer",
};
const SOURCE_LABEL = { DEFAULT: "Built-in defaults", GLOBAL: "Global rules", ORGANIZATION: "This organization's rules" } as const;

/**
 * Follow-ups: when Rogue picks a conversation back up on its own. What the seller asked for
 * ("next Friday") and the rules for quiet sellers. Global applies to every organization; an
 * organization can use its own instead. Only `prompts:manage` edits.
 */
export function FollowupSettingsEditor({ organizationId }: { organizationId: string | null }) {
  const q = useFollowupSettings(organizationId);
  return (
    <QueryState query={q} data={q.data} resource="follow-up settings">
      {(view) => <Editor key={`${view.updatedAt ?? "none"}-${view.custom}`} view={view} organizationId={organizationId} />}
    </QueryState>
  );
}

function Editor({ view, organizationId }: { view: FollowupSettingsView; organizationId: string | null }) {
  const { can } = usePermissions();
  const canEdit = can("prompts:manage");
  const inheriting = organizationId !== null && !view.custom;
  const [draft, setDraft] = useState<FollowupSettingsValue>(view.settings);
  const [customizing, setCustomizing] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const update = useUpdateFollowupSettings(organizationId);
  const reset = useResetFollowupSettings(organizationId);
  useEffect(() => setDraft(view.settings), [view.settings]);

  const editable = canEdit && (!inheriting || customizing);
  const dirty = customizing || JSON.stringify(draft) !== JSON.stringify(view.settings);
  const errors = { ...followupSettingsErrors(draft), ...serverErrors };
  const scope = organizationId ? (view.organization?.name ?? "this organization") : "every organization";
  const set = (patch: Partial<FollowupSettingsValue>) => {
    setServerErrors({});
    setDraft((d) => ({ ...d, ...patch }));
  };
  const setRule = (i: number, patch: Partial<FollowupRule>) => set({ rules: draft.rules.map((r, k) => (k === i ? { ...r, ...patch } : r)) });
  const move = (i: number, by: number) => {
    const rules = [...draft.rules];
    const [r] = rules.splice(i, 1);
    if (r) rules.splice(i + by, 0, r);
    set({ rules });
  };

  const save = () =>
    update.mutate(draft, {
      onSuccess: () => {
        setCustomizing(false);
        setFormError(null);
        toast.success("Follow-up rules saved", { description: `They apply to ${scope} from the next check.` });
      },
      onError: (err) => {
        const { fields, form } = formErrorsFrom(err);
        setServerErrors(Object.fromEntries(Object.entries(fields).map(([k, v]) => [k.replace(/^settings\./, ""), v])));
        setFormError(form);
      },
    });
  const backToInherited = () =>
    reset.mutate(undefined, {
      onSuccess: () => toast.success(organizationId ? "Back to the global rules" : "Back to the built-in defaults"),
      onError: (err) => toast.error("Couldn't reset the rules", { description: err.message }),
    });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={view.custom ? "default" : "outline"} data-testid="followup-source">
          {inheriting ? (view.source === "GLOBAL" ? "Inherits the global rules" : "Inherits the built-in defaults") : SOURCE_LABEL[view.source]}
        </Badge>
        {view.updatedAt ? (
          <span className="text-muted-foreground text-xs">
            Last changed {formatDateTime(view.updatedAt)}
            {view.updatedBy ? ` by ${view.updatedBy.name}` : ""}
          </span>
        ) : null}
        {canEdit && inheriting && !customizing ? (
          <Button size="xs" variant="outline" className="ml-auto" onClick={() => setCustomizing(true)}>
            <SquarePen data-icon="inline-start" />
            Customize for this organization
          </Button>
        ) : null}
        {canEdit && view.custom ? (
          <Button size="xs" variant="ghost" className="ml-auto" disabled={reset.isPending} onClick={backToInherited}>
            <RotateCcw data-icon="inline-start" />
            {organizationId ? "Use the global rules" : "Reset to the built-in defaults"}
          </Button>
        ) : null}
      </div>
      <p className="text-muted-foreground text-xs">
        Rogue texts again on its own when the seller asked to talk later, or when they've gone quiet and a rule matches (the first enabled rule that matches
        applies; when it runs out of attempts the next one takes over). Follow-ups go out inside each store's texting hours, never while a person handles the
        conversation, after a visit is booked or once the lead is closed. A reply from the seller cancels them.
      </p>
      {formError ? (
        <Alert variant="destructive">
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      ) : null}

      <Section title="Follow-ups">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={draft.enabled} disabled={!editable} onCheckedChange={(v) => set({ enabled: v })} aria-label="Follow-ups on" />
          Follow-ups on for {scope}
        </label>
      </Section>

      <Section title="When the seller asks to talk later">
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={draft.sellerRequested.enabled}
              disabled={!editable}
              onCheckedChange={(v) => set({ sellerRequested: { ...draft.sellerRequested, enabled: v } })}
              aria-label="Follow up when the seller asks"
            />
            Follow up on the day the seller names ("I'll get back to you next Friday")
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <NumberField
              label="Time when they name only a day"
              type="time"
              value={draft.sellerRequested.defaultTime}
              disabled={!editable}
              error={errors["sellerRequested.defaultTime"]}
              onChange={(v) => set({ sellerRequested: { ...draft.sellerRequested, defaultTime: v } })}
            />
            <NumberField
              label={`"Later" with no day: days out`}
              value={draft.sellerRequested.defaultDays}
              disabled={!editable}
              error={errors["sellerRequested.defaultDays"]}
              onChange={(v) => set({ sellerRequested: { ...draft.sellerRequested, defaultDays: Number(v) } })}
            />
            <NumberField
              label="At most this many days ahead"
              value={draft.sellerRequested.maxDaysAhead}
              disabled={!editable}
              error={errors["sellerRequested.maxDaysAhead"]}
              onChange={(v) => set({ sellerRequested: { ...draft.sellerRequested, maxDaysAhead: Number(v) } })}
            />
          </div>
        </div>
      </Section>

      <Section title="When the price is the only gap">
        <div className="space-y-3">
          <p className="text-muted-foreground text-xs">
            Rogue offered its maximum and the seller wants more (or the gap is small). Follow up: Rogue keeps the lead and restates its offer every few days
            until the gap closes. Hand off: a person takes over.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="space-y-1 text-xs">
              <span className="text-muted-foreground">Then</span>
              <NativeSelect
                aria-label="Price gap"
                value={draft.priceGap.action}
                disabled={!editable}
                onChange={(e) => set({ priceGap: { ...draft.priceGap, action: e.target.value as "FOLLOW_UP" | "HANDOFF" } })}
                className="w-full"
              >
                <NativeSelectOption value="FOLLOW_UP">Rogue follows up</NativeSelectOption>
                <NativeSelectOption value="HANDOFF">Hand off to a person</NativeSelectOption>
              </NativeSelect>
            </label>
            <NumberField
              label="Every (days)"
              value={draft.priceGap.everyDays}
              disabled={!editable || draft.priceGap.action !== "FOLLOW_UP"}
              error={errors["priceGap.everyDays"]}
              onChange={(v) => set({ priceGap: { ...draft.priceGap, everyDays: Number(v) } })}
            />
          </div>
        </div>
      </Section>

      <Section
        title={`Rules for quiet sellers (${draft.rules.length})`}
        actions={
          editable ? (
            <Button size="xs" variant="outline" onClick={() => set({ rules: [...draft.rules, newRule(draft.rules)] })} disabled={draft.rules.length >= 20}>
              <Plus data-icon="inline-start" />
              Add rule
            </Button>
          ) : null
        }
      >
        {draft.rules.length === 0 ? <p className="text-muted-foreground text-xs">No rules: quiet sellers aren't followed up.</p> : null}
        <ol className="space-y-3">
          {draft.rules.map((rule, i) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              index={i}
              count={draft.rules.length}
              stages={view.options.stages}
              errors={errors}
              editable={editable}
              onChange={(patch) => setRule(i, patch)}
              onMove={(by) => move(i, by)}
              onRemove={() => set({ rules: draft.rules.filter((_, k) => k !== i) })}
            />
          ))}
        </ol>
      </Section>

      {canEdit ? <FollowupCatchUp organizationId={organizationId} scope={scope} /> : null}

      {editable ? (
        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t bg-background/95 py-2 backdrop-blur">
          {Object.keys(errors).length ? <span className="mr-auto text-destructive text-xs">Fix the highlighted fields</span> : null}
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || update.isPending}
            onClick={() => {
              setDraft(view.settings);
              setCustomizing(false);
              setServerErrors({});
            }}
          >
            Discard
          </Button>
          <Button size="sm" disabled={!dirty || update.isPending || Object.keys(followupSettingsErrors(draft)).length > 0} onClick={save}>
            {update.isPending ? <Spinner data-icon="inline-start" /> : null}
            Save rules
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function RuleCard({
  rule,
  index,
  count,
  stages,
  errors,
  editable,
  onChange,
  onMove,
  onRemove,
}: {
  rule: FollowupRule;
  index: number;
  count: number;
  stages: string[];
  errors: Record<string, string>;
  editable: boolean;
  onChange: (patch: Partial<FollowupRule>) => void;
  onMove: (by: number) => void;
  onRemove: () => void;
}) {
  const at = (f: string) => errors[`rules.${index}.${f}`];
  const label = rule.name || `Rule ${index + 1}`;
  return (
    <li className="rounded-md border p-3" aria-label={`Rule ${index + 1}: ${label}`}>
      <div className="mb-3 flex items-center gap-2">
        <span className="text-muted-foreground text-xs tabular-nums">{index + 1}.</span>
        <Switch checked={rule.enabled} disabled={!editable} onCheckedChange={(v) => onChange({ enabled: v })} aria-label={`${label} enabled`} />
        <Input
          aria-label="Rule name"
          value={rule.name}
          disabled={!editable}
          onChange={(e) => onChange({ name: e.target.value })}
          aria-invalid={Boolean(at("name"))}
          className="h-7 max-w-xs"
        />
        {editable ? (
          <span className="ml-auto flex gap-1">
            <Button size="icon-xs" variant="ghost" disabled={index === 0} onClick={() => onMove(-1)} aria-label={`Move ${label} up`}>
              <ArrowUp />
            </Button>
            <Button size="icon-xs" variant="ghost" disabled={index === count - 1} onClick={() => onMove(1)} aria-label={`Move ${label} down`}>
              <ArrowDown />
            </Button>
            <Button size="icon-xs" variant="ghost" onClick={onRemove} aria-label={`Remove ${label}`}>
              <Trash2 />
            </Button>
          </span>
        ) : null}
      </div>
      {at("name") ? <p className="mb-2 text-destructive text-xs">{at("name")}</p> : null}
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Offer</span>
          <NativeSelect
            value={rule.when.offer}
            disabled={!editable}
            onChange={(e) => onChange({ when: { ...rule.when, offer: e.target.value as FollowupRule["when"]["offer"] } })}
            aria-label="Offer"
            className="w-full"
          >
            {(Object.keys(OFFER_LABEL) as Array<keyof typeof OFFER_LABEL>).map((k) => (
              <NativeSelectOption key={k} value={k}>
                {OFFER_LABEL[k]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </label>
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Max offer</span>
          <NativeSelect
            value={rule.when.maxOffer}
            disabled={!editable}
            onChange={(e) => onChange({ when: { ...rule.when, maxOffer: e.target.value as FollowupRule["when"]["maxOffer"] } })}
            aria-label="Max offer"
            className="w-full"
          >
            {(Object.keys(MAX_OFFER_LABEL) as Array<keyof typeof MAX_OFFER_LABEL>).map((k) => (
              <NativeSelectOption key={k} value={k}>
                {MAX_OFFER_LABEL[k]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </label>
        <NumberField
          label="Quiet for (days)"
          value={rule.when.silentDays}
          disabled={!editable}
          error={at("when.silentDays")}
          onChange={(v) => onChange({ when: { ...rule.when, silentDays: Number(v) } })}
        />
        <label className="space-y-1 text-xs">
          <span className="text-muted-foreground">Then</span>
          <NativeSelect
            value={rule.action}
            disabled={!editable}
            onChange={(e) => onChange({ action: e.target.value as FollowupRuleAction })}
            aria-label="Action"
            className="w-full"
          >
            {(Object.keys(ACTION_LABEL) as FollowupRuleAction[]).map((k) => (
              <NativeSelectOption key={k} value={k}>
                {ACTION_LABEL[k]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </label>
        <NumberField
          label="Attempts"
          value={rule.maxAttempts}
          disabled={!editable}
          error={at("maxAttempts")}
          onChange={(v) => onChange({ maxAttempts: Number(v) })}
        />
        <NumberField
          label="Days between attempts"
          value={rule.repeatEveryDays}
          disabled={!editable || rule.maxAttempts <= 1}
          error={at("repeatEveryDays")}
          onChange={(v) => onChange({ repeatEveryDays: Number(v) })}
        />
        <div className="space-y-1 text-xs">
          <label className="flex items-center gap-2 text-muted-foreground">
            <input
              type="checkbox"
              checked={rule.markUnresponsiveAfterDays !== null}
              disabled={!editable}
              onChange={(e) => onChange({ markUnresponsiveAfterDays: e.target.checked ? 7 : null })}
              aria-label="Close as unresponsive after the last attempt"
            />
            Then close the lead as unresponsive after (days)
          </label>
          <Input
            type="number"
            aria-label="Close after (days)"
            value={rule.markUnresponsiveAfterDays ?? ""}
            disabled={!editable || rule.markUnresponsiveAfterDays === null}
            onChange={(e) => onChange({ markUnresponsiveAfterDays: Number(e.target.value) })}
            aria-invalid={Boolean(at("markUnresponsiveAfterDays"))}
            className="h-7"
          />
          {at("markUnresponsiveAfterDays") ? <p className="text-destructive">{at("markUnresponsiveAfterDays")}</p> : null}
        </div>
      </div>
      <fieldset className="mt-3 space-y-1">
        <legend className="text-muted-foreground text-xs">Lead stages (none selected: any stage before an agreement)</legend>
        <div className="flex flex-wrap gap-1">
          {stages.map((s) => {
            const on = rule.when.stages.includes(s);
            return (
              <Button
                key={s}
                size="xs"
                variant={on ? "default" : "outline"}
                aria-pressed={on}
                disabled={!editable}
                onClick={() => onChange({ when: { ...rule.when, stages: on ? rule.when.stages.filter((x) => x !== s) : [...rule.when.stages, s] } })}
              >
                {STAGE_LABEL(s)}
              </Button>
            );
          })}
        </div>
      </fieldset>
      <label className="mt-3 block space-y-1 text-xs">
        <span className="text-muted-foreground">Instructions for Rogue's text (optional)</span>
        <Textarea
          value={rule.instructions ?? ""}
          disabled={!editable}
          rows={2}
          maxLength={500}
          onChange={(e) => onChange({ instructions: e.target.value.trim() ? e.target.value : null })}
          aria-label="Instructions"
          placeholder="Mention we buy cars with a loan on them too."
        />
        {at("instructions") ? <span className="text-destructive">{at("instructions")}</span> : null}
      </label>
      {at("id") ? <p className="mt-2 text-destructive text-xs">{at("id")}</p> : null}
    </li>
  );
}

function NumberField({
  label,
  value,
  disabled,
  error,
  onChange,
  type = "number",
}: {
  label: string;
  value: number | string;
  disabled: boolean;
  error?: string;
  onChange: (v: string) => void;
  type?: "number" | "time";
}) {
  return (
    <label className="space-y-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <Input
        type={type}
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        className="h-7"
      />
      {error ? <span className="text-destructive">{error}</span> : null}
    </label>
  );
}
